import {Component, computed, inject, input, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ChartData, ChartOptions} from 'chart.js';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {TranslocoService} from '@jsverse/transloco';
import {ProgressDashboardChartContainerComponent} from '../progress-dashboard-chart-container/progress-dashboard-chart-container.component';
import {SubCriteriaAttributeItem} from '../../subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.types';

export interface BarChartConfig<TCriteria> {
  /**
   * Function to extract the number identifier from attribute (e.g., ecNumber or scNumber)
   */
  getNumberFromAttribute: (attribute: SubCriteriaAttributeItem) => number | undefined;

  /**
   * Function to find the criteria item by number
   */
  findCriteriaByNumber: (criteria: TCriteria[], number: number) => TCriteria | undefined;

  /**
   * Function to extract translations from criteria
   */
  getTranslations: (criteria: TCriteria) => any;

  /**
   * Sort direction (default is descending)
   */
  sortDescending?: boolean;
}

@Component({
  selector: 'app-progress-dashboard-generic-bar-chart',
  imports: [ProgressDashboardChartContainerComponent],
  template: `
    <app-progress-dashboard-chart-container
      [label]="label()"
      [data]="data()"
      [options]="options()"
      type="bar"
    />
  `,
  styleUrls: ['./progress-dashboard-generic-bar-chart.component.scss'],
})
export class ProgressDashboardGenericBarChartComponent<TCriteria> {
  readonly label = input.required<string>();
  readonly subCriteriaAttributes = input.required<SubCriteriaAttributeItem[]>();
  readonly criteria = input.required<TCriteria[]>();
  readonly config = input.required<BarChartConfig<TCriteria>>();

  private readonly translateDirectusPipe = new TranslateDirectusPipe();
  private readonly translocoService = inject(TranslocoService);

  private readonly activeLang = toSignal(this.translocoService.langChanges$);

  readonly data = computed<ChartData<'bar'> | null>(() => {
    const attributeData = this.subCriteriaAttributes();
    const criteriaList = this.criteria();
    const chartConfig = this.config();

    const numbers = Array.from(
      new Set(
        attributeData
          .map((attribute) => chartConfig.getNumberFromAttribute(attribute))
          .filter((num): num is number => num !== undefined),
      ),
    );

    const sortedNumbers = this.sortNumbers(numbers, chartConfig.sortDescending);

    this.activeLang();

    const labels = sortedNumbers.map((number) => {
      const criteriaItem = chartConfig.findCriteriaByNumber(criteriaList, number);
      return criteriaItem
        ? (this.translateDirectusPipe.transform(chartConfig.getTranslations(criteriaItem)) ?? '')
        : '';
    });

    const completedData = sortedNumbers.map((number) => {
      const total = this.calcTotalByNumber(number, chartConfig);
      const completed = this.calcCompletedByNumber(number, chartConfig);
      return total > 0 ? Math.round((completed / total) * 100) : 0;
    });

    const notCompletedData = sortedNumbers.map((number) => {
      const total = this.calcTotalByNumber(number, chartConfig);
      const completed = this.calcCompletedByNumber(number, chartConfig);
      return total > 0 ? 100 - Math.round((completed / total) * 100) : 0;
    });

    const colors = this.getChartColors(2);

    return {
      labels: labels,
      datasets: [
        {
          label: 'Completed',
          data: completedData,
          backgroundColor: colors[0],
        },
        {
          label: 'Not Completed',
          data: notCompletedData,
          backgroundColor: colors[1],
        },
      ],
    };
  });

  protected options = signal<ChartOptions>({
    animations: undefined,
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        color: 'white',
        font: {
          weight: 'bold',
        },
        formatter: (value: number) => {
          return value > 0 ? `${value}%` : '';
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        max: 100,
        ticks: {
          color: 'white',
        },
        grid: {
          color: 'rgb(107, 114, 128, 0.3)',
        },
      },
      y: {
        type: 'category',
        stacked: true,
        ticks: {
          color: 'white',
        },
        grid: {
          color: 'rgb(107, 114, 128, 0.3)',
        },
      },
    },
  });

  private sortNumbers(numbers: number[], descending = true): number[] {
    return descending ? numbers.sort((a, b) => b - a) : numbers.sort((a, b) => a - b);
  }

  private calcCompletedByNumber(number: number, config: BarChartConfig<TCriteria>): number {
    const completed = this.subCriteriaAttributes()
      .filter((attribute) => config.getNumberFromAttribute(attribute) === number)
      .filter((attribute: any) => {
        // Prefer generic completion flag if present
        if (attribute?.isCompleted !== undefined) return Boolean(attribute.isCompleted);

        // Backward-compatible fallback
        return attribute?.selectedImportanceScore !== undefined;
      });

    return new Set(completed).size;
  }

  private calcTotalByNumber(number: number, config: BarChartConfig<TCriteria>): number {
    return new Set(
      this.subCriteriaAttributes().filter(
        (attribute) => config.getNumberFromAttribute(attribute) === number,
      ),
    )?.size;
  }

  private getChartColors(count: number): string[] {
    const documentStyle = getComputedStyle(document.documentElement);
    const colors = [documentStyle.getPropertyValue('--color-green'), 'rgb(107, 114, 128)'];
    return colors.slice(0, count);
  }
}
